import { useEffect, useState } from "react";
import { IDropdown, ILeadReq } from "../../../models/BaseModel";
import { JVMultiselect } from "../../../shared/JVMultiselect";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Fetch } from "../../../shared/Fetch";
import { Dialog } from "primereact/dialog";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { JVInput } from "../../../shared/JVInput";

interface IRooms {
    roomTypeId: number
    count: number
    mattress: number
}
interface IRoomTypeDrop extends IDropdown {
    capacity: number
    mattress: number
    mattressPrice: number
    date: string | Date
    minimumStay: number
    extraAdultPrice: number
    extraChildPrice: number
    prices?: {
        sharing: number
        price: number
    }[]
}
export interface IAccommodationReq {
    vegAdult?: number
    nonVegAdult?: number
    jainAdult?: number
    vegChild?: number
    nonVegChild?: number
    jainChild?: number,
    rooms?: {
        roomTypeId: number
        count: number
        mattress: number
        price: number
        mattressPrice: number
    }[]
}

interface IProps {
    req: ILeadReq
    open: boolean
    onSuccess: () => void
    onHide: () => void
    toast: any
}

const schema = yup.object({
    vegAdult: yup.number().typeError('Required').required("Required").min(0, "Rrequired"),
    nonVegAdult: yup.number().typeError('Required').required("Required").min(0, "Required"),
    jainAdult: yup.number().typeError('Required').required("Required").min(0, "Required"),
    vegChild: yup.number().typeError('Required').required("Required").min(0, "Required"),
    nonVegChild: yup.number().typeError('Required').required("Required").min(0, "Required"),
    jainChild: yup.number().typeError('Required').required("Required").min(0, "Required"),
});

export const Room = ({ req, toast, onSuccess, open, onHide }: IProps) => {
    const [roomTypes, setRoomTypes] = useState<IRoomTypeDrop[]>();
    const [rooms, setRooms] = useState<IRooms[]>();
    const [loading, setLoading] = useState(false);

    const { control, handleSubmit, watch, formState: { errors }, setValue, reset } = useForm<IAccommodationReq>({
        resolver: yupResolver(schema),
        defaultValues: {},
        shouldFocusError: false,
    });

    useEffect(() => { if (open) reset(req) }, [req]);

    const vegAdult = watch("vegAdult") || 0;
    const nonVegAdult = watch("nonVegAdult") || 0;
    const vegChild = watch("vegChild") || 0;
    const nonVegChild = watch("nonVegChild") || 0;

    useEffect(() => {
        if (req.noOfAdults && req.noOfAdults > 0 && vegAdult && nonVegAdult) {
            const jainAdult = req.noOfAdults - (+vegAdult + +nonVegAdult);
            setValue('jainAdult', jainAdult < 0 ? 0 : jainAdult);
        }
    }, [vegAdult, nonVegAdult, req.noOfAdults]);
    useEffect(() => {
        if (req.noOfChildren && req.noOfChildren > 0 && vegChild && nonVegChild) {
            const jainChild = req.noOfChildren - (+vegChild + +nonVegChild);
            setValue('jainChild', jainChild < 0 ? 0 : jainChild);
        }
    }, [vegChild, nonVegChild, req.noOfChildren]);

    useEffect(() => {
        if (open) setRooms(req.rooms);
    }, [req.rooms]);

    const loadRoomTypes = async (_hotelId: number, mealPlan: string) => {
        setLoading(true);
        const response = await Fetch(`roomtype/dropdown/${_hotelId}`, "GET", null, `mealPlan=${mealPlan}`, toast);
        if (response) {
            setRoomTypes(await response.json());
        }
        setLoading(false);
    }
    useEffect(() => {
        if (open && req.hotelId && req.mealPlan) loadRoomTypes(req.hotelId, req.mealPlan);
        else setRoomTypes(undefined);
    }, [req.hotelId, req.mealPlan]);

    const onRoomsChange = (newRooms: any[]) => {
        setRooms(prev => {
            return newRooms.map((r: any) => {
                const existing = prev?.find(p => p.roomTypeId === r.id);
                const count = existing?.count && existing.count > 0 ? existing.count : 1;
                const mattress = existing?.mattress && existing.mattress > 0 ? existing.mattress : 0;
                return { roomTypeId: r.id, count, mattress };
            });
        });
    };

    function addCountRoom(room: IRooms) {
        const updated = rooms?.map(r =>
            r.roomTypeId === room.roomTypeId ? { ...r, count: r.count + 1 } : r
        );
        setRooms(updated);
    }
    function removeCountRoom(room: IRooms) {
        const updated = rooms
            ?.map(r => r.roomTypeId === room.roomTypeId ? { ...r, count: r.count - 1 } : r)
            .filter(r => r.count > 0);
        setRooms(updated);
    }


    function addMattress(room: IRooms) {
        const rt = roomTypes?.find(rt => rt.id === room.roomTypeId);
        const r = rooms?.find(r => r.roomTypeId === room.roomTypeId);
        if (rt && r) {
            const totalAllowedMattress = rt.mattress * r.count;
            if (totalAllowedMattress < r.mattress + 1) {
                if (totalAllowedMattress > 0)
                    toast.current.show({ severity: 'error', detail: `Can't add more than ${totalAllowedMattress} mattress.`, life: 3000 });
                else
                    toast.current.show({ severity: 'error', detail: `Extra mattress not allowed with this room-type.`, life: 3000 });
            } else {
                const updated = rooms?.map(r =>
                    r.roomTypeId === room.roomTypeId ? { ...r, mattress: r.mattress + 1 } : r
                );
                setRooms(updated);
            }
        }
    }
    function removeMattress(room: IRooms) {
        const updated = rooms?.map(r =>
            r.roomTypeId === room.roomTypeId ? { ...r, mattress: r.mattress - 1 > 0 ? r.mattress - 1 : 0 } : r
        );
        setRooms(updated);
    }
    function peopleleftroom(): number {
        if (rooms && rooms.length > 0) {
            const totalCapacity = rooms.reduce((acc, r) => {
                const capacity = roomTypes?.find(rt => rt.id === r.roomTypeId)?.capacity || 0;
                return acc + (capacity * r.count) + r.mattress;
            }, 0);
            return (req.noOfAdults ?? 0) + (req.noOfChildren ?? 0) - totalCapacity;
        }
        return (req.noOfAdults ?? 0) + (req.noOfChildren ?? 0);
    }

    async function saveToDB(body: IAccommodationReq) {
        setLoading(true);
        if (peopleleftroom() > 0) {
            toast.current.show({ severity: 'error', detail: `Please select room types for ${peopleleftroom()} more people`, life: 3000 });
            setLoading(false);
            return;
        }
        body.rooms = rooms?.map(({ roomTypeId, count, mattress }) => {
            const capacity = roomTypes?.find(e => e.id === roomTypeId)?.capacity ?? 0;
            const price = roomTypes?.find(e => e.id === roomTypeId)?.prices?.find(f => f.sharing === capacity)?.price ?? 0;
            const mattressPrice = roomTypes?.find(rt => rt.id === roomTypeId)?.mattressPrice || 0;
            return { roomTypeId, count, mattress, price, mattressPrice };
        });
        const response = await Fetch(`leadreq/accommodation/${req.id}`, 'PUT', body, null, toast);
        if (response) {
            toast.current.show({ severity: 'success', detail: `Accommodation updated`, life: 3000 });
            onSuccess();
        }
        setLoading(false);
    }

    function onSubmit(data: IAccommodationReq) {
        saveToDB(data);
    }


    return (
        <Dialog visible={open} style={{ width: '80vw' }} header={`Add/Edit Accommodation (${req.cityName}, ${req.stateName} | ${req.hotelName} | ${req.mealPlan}) (${req.noOfAdults} Adults, ${req.noOfChildren} Children)`} modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                <JVMultiselect column={1} label='Select Room Types' options={roomTypes} optionLabel="name" placeholder="Select Room Types" required value={roomTypes?.filter(rt => rooms?.map(e => e.roomTypeId).includes(rt.id))} onChange={e => onRoomsChange(e)} />
                {peopleleftroom() > 0 && <div className="field col-12 md:col-12"><span className="p-error">Please add rooms for {peopleleftroom()} more people</span></div>}
                {rooms?.map((r) =>
                    <div key={r.roomTypeId} className="field col-12 md:col-3">
                        <div className="card flex-1 p-0" style={{ width: '250px', position: 'relative', backgroundImage: 'linear-gradient(to bottom, #ffffff, #f0f0f0)' }}>
                            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', padding: '10px' }}>
                                <label>{roomTypes?.find(e => e.id === r.roomTypeId)?.name}</label>
                                <div>{(() => {
                                    const capacity = roomTypes?.find(e => e.id === r.roomTypeId)?.capacity ?? 0;
                                    const price = roomTypes?.find(e => e.id === r.roomTypeId)?.prices?.find(f => f.sharing === capacity)?.price ?? 0;
                                    return <small className="p-info" >{`${capacity} people | ${price} rs/day | ${roomTypes?.find(e => e.id === r.roomTypeId)?.mattress} mattress/room`}</small>
                                })()}</div>
                                <div className="p-inputgroup flex-1">
                                    <Button style={{ height: '20px', width: '20px', margin: '5px' }} onClick={() => removeMattress(r)} icon="pi pi-minus" type="button" className="p-button-text p-button-secondary" />
                                    <div style={{ display: "flex", alignItems: 'center', justifyContent: 'center' }}>Mattress: {r.mattress}</div>
                                    <Button style={{ height: '20px', width: '20px', margin: '5px' }} onClick={() => addMattress(r)} icon="pi pi-plus" type="button" className="p-button-text p-button-secondary" />
                                </div>
                            </div>
                            <Badge style={{ position: 'absolute', top: -5, right: -5 }} value={r.count} severity="warning"></Badge>
                            <div className="flex" style={{ position: 'absolute', bottom: 0, right: 0 }}>
                                <Button style={{ height: '20px', width: '20px', margin: '5px' }} onClick={() => removeCountRoom(r)} icon="pi pi-minus" type="button" className="p-button-danger" />
                                <Button style={{ height: '20px', width: '20px', margin: '5px' }} onClick={() => addCountRoom(r)} icon="pi pi-plus" type="button" className="p-button-success" />
                            </div>
                        </div>
                    </div>)}

                {req.mealPlan && req.mealPlan !== 'EP' &&
                    <>
                        <div className="field col-12 md:col-12 p-fluid formgrid grid px-1 m-0 mt-3">
                            <div className="field col-12 md:col-2">Adults :</div>
                            <div className="field col-12 md:col-10 p-fluid formgrid grid p-0 m-0">
                                <JVInput column={3} type="number" label="Vegetarian" control={control} name="vegAdult" error={errors.vegAdult?.message} />
                                <JVInput column={3} type="number" label="Non Vegetarian" control={control} name="nonVegAdult" error={errors.nonVegAdult?.message} />
                                <JVInput column={3} type="number" label="Jain" control={control} name="jainAdult" error={errors.jainAdult?.message} />
                            </div>
                        </div>

                        {(req.noOfChildren && req.noOfChildren > 0) ? <div className="field col-12 md:col-12 p-fluid formgrid grid px-1 m-0">
                            <div className="field col-12 md:col-2">Children :</div>
                            <div className="field col-12 md:col-10 p-fluid formgrid grid p-0 m-0">
                                <JVInput column={3} type="number" label="Vegetarian" control={control} name="vegChild" error={errors.vegChild?.message} />
                                <JVInput column={3} type="number" label="Non Vegetarian" control={control} name="nonVegChild" error={errors.nonVegChild?.message} />
                                <JVInput column={3} type="number" label="Jain" control={control} name="jainChild" error={errors.jainChild?.message} />
                            </div>
                        </div> : ''}
                    </>
                }

                <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                    <Button style={{ width: '100px' }} loading={loading} label="SAVE" type="submit" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </form>
        </Dialog>
    );
}